import { WSFormProps, WSList, WSText } from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { useUserId } from "../../../../query/hooks/helpers";
import {
  useQueryMemberProfile,
  useQueryUserProfile
} from "../../../../query/users/queries";
import { verificationService } from "../../../../shared/services/verificaiton";
import { CardTaxVerificationRequirementRequester } from "../../components/CardTaxVerificationRequirementRequester";
import { getProfileDataPerson } from "../../components/FormPartialPerson/getProfileDataPerson";
import {
  FormDataPersonalInformation,
  FormPersonalInformation
} from "../../components/FormPersonalInformation";
import { useOnboardignModules, useOnboardingContext } from "../../Context";
import { useMissingFields } from "../../hooks/useMissingFields";
import { OnboardingModuleCreateAccount } from "../../types";
import { CreateAccountContext } from "./types";
import { useRedirectToAccountType } from "./useRedirectToAccountType";
import { useQueryVerifications } from "../../../../query/onboarding/queries/useQueryVerifications";
import { useQueryUserProfileV2 } from "../../../../query/user/queries/useQueryUserProfile";
import { FlowStep } from "../../../../shared/components/FlowSwitch/types";

export const PersonalInformation: React.FC<FlowStep> = ({ onNext, onBack }) => {
  const [context, setContext] = useOnboardingContext<CreateAccountContext>();

  const userId = useUserId();
  const queryUser = useQueryUserProfile(userId);
  const queryMember = useQueryMemberProfile(userId);
  const queryVerifications = useQueryVerifications();
  const queryUserV2 = useQueryUserProfileV2();

  const profileData: WSFormProps<FormDataPersonalInformation>["defaultValues"] =
    useMemo(
      () => ({
        person: getProfileDataPerson({
          type: "Individual",
          user: queryUser.data,
          member: queryMember.data,
          userV2: queryUserV2.data
        })
      }),
      [queryMember.data, queryUser.data, queryUserV2.data]
    );

  const modules = useOnboardignModules();
  const onboardingModule = modules.find(m => m.type === "create_account") as
    | OnboardingModuleCreateAccount
    | undefined;
  const verificationLevel =
    onboardingModule?.options?.verificationLevel || "Tax";

  const missingFields = useMissingFields(verificationLevel);

  const isMismatch = useMemo(
    () => verificationService.getIsIndividualMismatch(missingFields),
    [missingFields]
  );

  const isCompleted = useMemo(
    () =>
      queryVerifications.data
        ? verificationService.getVerificationStatus(
            verificationLevel,
            queryVerifications.data
          ) === "Verified"
        : false,
    [queryVerifications.data, verificationLevel]
  );

  useRedirectToAccountType();

  return (
    <WSList gap="2XL">
      <CardTaxVerificationRequirementRequester />

      <FormPersonalInformation
        defaultValues={context.personalInformation || profileData}
        onBack={onBack}
        onSubmit={data => {
          isCompleted ||
            setContext(prev => ({
              ...prev,
              personalInformation: data
            }));
          onNext?.();
        }}
        mismatch={isMismatch}
        country={context.accountType?.country || "US"}
        completed={isCompleted}
      />

      <WSText.ParagraphXs color="gray400">
        The information on this page is required to setup your Wingspan account,
        if you exit or leave without completing this step, you will return to
        this page the next time you sign-in
      </WSText.ParagraphXs>
    </WSList>
  );
};
